import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { ComponentType } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import svg from "../../utils/svg"
import { formatCurrency } from "../../utils/utils"
import styles from "./TemperedPaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Tempered" — a NON-ONBOARDING, in-app upgrade prompt for an existing free
// user, built under a "security & privacy" directive. The concept is
// DEFENCE-IN-DEPTH made LITERAL and MATERIAL: your memories are set behind
// accreting sheets of tempered privacy glass. You always see straight through
// to your own photos, warm and clear; the depth is what everyone else has to
// get through.
//
// Behavioural spine: ENDOWMENT + MATERIAL SOLIDITY (not progress/gap-closing).
// The message is never "you are exposed, close the gaps." It is "your memories
// deserve to sit behind more than a single pane — laminate them into something
// solid." The base pane (the on-device passcode you already have, free) is
// shown already set — an honest endowment — and Premium is the extra depth that
// makes the block feel substantial. People value substance they can see and
// feel; the deepening glass makes protection tangible instead of abstract.
//
// Nothing is reused from another paywall. The stage is not a hero image, not a
// feature checklist, not Ironclad's flat audit console with a coverage rail and
// a scan-line and an "X of 7" meter and a stamp. It is a single, three-
// dimensional GLASS BLOCK seen slightly from the front, panes stacking upward
// into visible thickness, each pane a real safeguard etched into the material,
// the photos glowing at its heart. The mechanic (light-refraction "set" of each
// pane building depth, a tap that rings the glass with a caustic shimmer) and
// the register (calm materials/craft, "solid, substantial, quiet") are new.
// Only the house fonts, colour tokens and existing feature icons are reused.
//
// Yearly is the hero: largest price, per-month broken out, honest Save %, the
// default selection, the 7-day trial. Monthly is a small secondary line. Copy
// is App-Store compliant: the base protection stays on whatever happens, photos
// are never said to be lost, there is no countdown or scarcity, and the savings
// claim is real (yearly vs. 12× monthly).
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
const photo = (n: number) => `${PUB}/tempered/p${n}.jpg`

// The vault the glass is protecting. Debug data ships zeros, so these anchor the
// concept on plain renders (the native app would inject the real figures).
const NUM_PHOTOS = 1362
const NUM_ALBUMS = 28
const CORE_PHOTOS = [1, 2, 3, 4, 5, 6]

type Pane = {
  key: string
  Icon: ComponentType<{ className?: string }>
  label: string
  note: string
  premium: boolean
}

// Innermost (closest to the photos) → outermost. The first pane is the free,
// on-device passcode the user already has: an honest endowment, shown already
// set. The four Premium panes are the real depth that laminates on top.
const PANES: Pane[] = [
  {
    key: "passcode",
    Icon: svg.featureIcons.PatternLock,
    label: "Passcode lock",
    note: "Opens only with your code",
    premium: false,
  },
  {
    key: "faceid",
    Icon: svg.featureIcons.SecurityPlus,
    label: "Only-You Unlock",
    note: "Face ID / Touch ID — no code to leak",
    premium: true,
  },
  {
    key: "backup",
    Icon: svg.featureIcons.CloudVault,
    label: "Encrypted backup",
    note: "A sealed copy only your device can open",
    premium: true,
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    label: "Decoy vault",
    note: "A second code opens a harmless album",
    premium: true,
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    label: "Break-in report",
    note: "A photo of anyone who tries a wrong code",
    premium: true,
  },
]

const TOTAL = PANES.length // 5
const BASE = PANES.filter((p) => !p.premium).length // 1 pane already yours

function TemperedPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""

  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  // `set` = how many panes are laminated on, counted from the base up. The base
  // pane is always set (endowment). On load the Premium panes settle one by one
  // to full depth so the block reads substantial straight away.
  const [set, setSet] = useState(reduce ? TOTAL : BASE)
  const [committed, setCommitted] = useState(false) // CTA pressed
  const [ring, setRing] = useState(0) // bump to replay the caustic shimmer
  const [tilt, setTilt] = useState({ x: 0, y: 0 }) // pointer parallax
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [showClose, setShowClose] = useState(false)

  const timers = useRef<number[]>([])
  const push = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms))
  }, [])

  useEffect(() => {
    push(() => setShowClose(true), reduce ? 0 : 1200)

    if (reduce) {
      setSet(TOTAL)
      return () => timers.current.forEach(window.clearTimeout)
    }

    // laminate the Premium panes on, one by one, building depth
    for (let i = BASE; i < TOTAL; i++) {
      push(
        () => {
          setSet(i + 1)
          setRing((r) => r + 1)
        },
        460 + (i - BASE) * 520,
      )
    }

    return () => timers.current.forEach(window.clearTimeout)
  }, [reduce, push])

  // Tapping the block "rings" the glass — a caustic light sweep — and, if depth
  // is not yet full, sets the next pane. A light, exploratory beat.
  const tapBlock = useCallback(() => {
    setRing((r) => r + 1)
    if (!reduce) setSet((s) => Math.min(TOTAL, s + 1))
  }, [reduce])

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (reduce) return
      const r = e.currentTarget.getBoundingClientRect()
      const px = (e.clientX - r.left) / r.width - 0.5
      const py = (e.clientY - r.top) / r.height - 0.5
      setTilt({ x: py * -7, y: px * 9 })
    },
    [reduce],
  )
  const onPointerLeave = useCallback(() => setTilt({ x: 0, y: 0 }), [])

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    setCommitted(true)
    setSet(TOTAL)
    setRing((r) => r + 1)
    push(() => {
      if (product) native.subscribe(product)
    }, 640)
  }, [plan, yearly, monthly, push])

  const handleClose = () => native.postNativeEvent("close")

  // The outermost pane currently set — named under the readout.
  const topIdx = Math.min(set, TOTAL) - 1
  const topPane = PANES[Math.max(0, topIdx)]
  const full = set >= TOTAL

  const ctaSub =
    plan === "yearly"
      ? `${yearlyPrice}/year after a 7-day free trial — just ${perMonth}/mo. Renews yearly, cancel anytime.`
      : `${monthlyPrice}/month. Renews monthly, cancel anytime.`

  return (
    <div
      className={`${styles.root} ${full ? styles.rootFull : ""}`}
      data-committed={committed ? "true" : "false"}
    >
      <div className={styles.wash} aria-hidden>
        <span className={styles.glowA} />
        <span className={styles.glowB} />
        <span className={styles.grain} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 8, paddingBottom: bottomInset + 14 }}
      >
        {/* Top bar */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmMark} aria-hidden>
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>
            <span className={styles.eyebrowDot} />
            Your memories, made solid
          </p>
          <h1 className={styles.title}>
            Set them
            <br />
            <span className={styles.titleGlass}>in glass.</span>
          </h1>
          <p className={styles.sub}>
            You already keep these on this device, behind your code. Premium
            laminates on real layers of privacy — so your photos sit deep in
            something solid, and only you can reach in.
          </p>
        </header>

        {/* ── The glass block: photos at the heart, panes stacking to depth ── */}
        <section className={styles.stageWrap} aria-label="Layers of protection">
          <div
            className={`${styles.stage} ${ring ? styles.stageRing : ""}`}
            key={ring}
            onClick={tapBlock}
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerLeave}
            role="button"
            tabIndex={0}
            aria-label="Tempered glass block — tap to add depth"
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") tapBlock()
            }}
          >
            <div
              className={styles.slab}
              style={{
                transform: `perspective(900px) rotateX(${18 + tilt.x}deg) rotateZ(0deg) rotateY(${tilt.y}deg)`,
              }}
            >
              {/* the memories, at the heart of the block */}
              <div className={styles.core}>
                <div className={styles.coreGrid}>
                  {CORE_PHOTOS.map((n) => (
                    <span key={n} className={styles.coreCell}>
                      <img
                        src={photo(n)}
                        alt=""
                        draggable={false}
                        loading="eager"
                      />
                    </span>
                  ))}
                </div>
                <span className={styles.coreWarm} aria-hidden />
              </div>

              {/* the panes — innermost first, each a real safeguard */}
              {PANES.map((pane, i) => {
                const on = i < set
                return (
                  <div
                    key={pane.key}
                    className={[
                      styles.pane,
                      pane.premium ? styles.panePremium : styles.paneBase,
                      on ? styles.paneOn : styles.paneOff,
                    ].join(" ")}
                    style={{
                      // stack upward into visible thickness
                      ["--depth" as any]: i,
                      ["--lift" as any]: `${-i * 15}px`,
                      zIndex: 10 + i,
                    }}
                  >
                    <span className={styles.paneFace} aria-hidden />
                    <span className={styles.paneEdge} aria-hidden />
                    <span className={styles.paneSweep} aria-hidden />
                    {on && (
                      <span className={styles.paneTag}>
                        <span className={styles.paneTagIcon}>
                          <pane.Icon />
                        </span>
                        <span className={styles.paneTagText}>{pane.label}</span>
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Readout — layers of protection (no fixed denominator: this is
              about growing depth, not completing a task) */}
          <div className={styles.readout}>
            <span className={styles.readNum} key={set}>
              {set}
            </span>
            <span className={styles.readText}>
              <span className={styles.readTop}>
                layer{set === 1 ? "" : "s"} of protection
              </span>
              <span className={styles.readNote}>
                <b>{topPane.label}</b> — {topPane.note}
              </span>
            </span>
          </div>

          {/* what is being protected — brand clarity */}
          <p className={styles.holds}>
            <span className={styles.holdsLock} aria-hidden>
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            Holding <b>{NUM_PHOTOS.toLocaleString()}</b> photos ·{" "}
            <b>{NUM_ALBUMS}</b> albums
          </p>
        </section>

        {/* ── Plans — yearly is the hero ─────────────────────────────────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>
                Best value · Save {savings}%
              </span>
            )}
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planName}>Yearly</span>
                <span className={styles.planCovers}>
                  every layer · 7-day free trial
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceRow}>
                  <span className={styles.priceBig}>{yearlyPrice}</span>
                  <span className={styles.pricePer}>/yr</span>
                </span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planNameSm}>Monthly</span>
                <span className={styles.planCoversSm}>one month at a time</span>
              </span>
              <span className={styles.planPriceSm}>
                <span className={styles.priceSm}>{monthlyPrice}</span>
                <span className={styles.pricePerSm}>/mo</span>
              </span>
            </span>
          </button>
        </section>

        {/* CTA */}
        <div className={styles.ctaDock}>
          <button
            className={`${styles.cta} ${committed ? styles.ctaCommitted : ""}`}
            onClick={purchase}
          >
            <span className={styles.ctaLabel}>
              {committed ? "Setting them in glass…" : "Set them in glass"}
            </span>
            {!committed && (
              <svg
                className={styles.ctaArrow}
                viewBox="0 0 24 24"
                width="19"
                height="19"
                aria-hidden
              >
                <path
                  d="M5 12h13M12 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your hidden photos are already private on this device. Premium adds
            extra layers of protection — it never changes what you have already
            secured, and your library is always yours to keep.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TemperedPaywall
